import { format, isValid, parse } from 'date-fns';

// Matches the PerfectRx invoice PDF's teal branding, distinct from the app chrome's primary color.
export const INVOICE_ACCENT_COLOR = 'rgb(52, 114, 141)';

export const DEFAULT_CUSTOMER_MESSAGE =
  'Make payment by the due date above. For billing questions, contact ap@perfectrx.com.';

export const EMPTY_LINE = { drugName: '', ndc: '', description: '', quantity: '', unitCost: '' };

export const lineAmount = (line) => (Number(line.quantity) || 0) * (Number(line.unitCost) || 0);

export const currency = (value) => `$${Number(value).toFixed(2)}`;

// The API takes calendar dates (YYYY-MM-DD). Formatting the local date parts directly
// avoids toISOString(), which would shift the day backwards for negative-offset timezones.
export const toApiDate = (date) => (date ? format(date, 'yyyy-MM-dd') : undefined);

// The API sends invoice dates as midnight UTC ("2025-05-01T00:00:00Z"), but they are calendar
// dates. new Date() would convert to local time and show the previous day west of UTC (every US
// timezone), so read only the YYYY-MM-DD part.
export const formatApiDate = (value) => {
  if (!value) return '';
  const date = parse(String(value).slice(0, 10), 'yyyy-MM-dd', new Date());
  return isValid(date) ? format(date, 'MM/dd/yyyy') : '';
};

export const isBlank = (value) => value == null || String(value).trim() === '';

export const isEmptyLine = (line) =>
  isBlank(line.drugName) &&
  isBlank(line.ndc) &&
  isBlank(line.description) &&
  isBlank(line.quantity) &&
  isBlank(line.unitCost);

const isWholeNumber = (value) => !isBlank(value) && /^\d+$/.test(value);
const isPositiveWholeNumber = (value) => isWholeNumber(value) && Number(value) > 0;
const isDecimalNumber = (value) => !isBlank(value) && /^\d+(\.\d+)?$/.test(value);
// The PDF prints Rate to the cent, so a sub-cent rate would print a line that isn't Qty x Rate.
const isCentAmount = (value) => /^\d+(\.\d{1,2})?$/.test(value);

// Restricts keystrokes to digits only, so quantity can never contain a decimal point.
export const sanitizeIntegerInput = (value) => value.replace(/\D/g, '');
// Restricts keystrokes to digits, at most one decimal point and at most two decimal places
// (e.g. blocks "25.0.1", "25a" or "12.345").
export const sanitizeDecimalInput = (value) =>
  value
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1')
    .replace(/(\.\d{2})\d+/, '$1');

export const lineFieldError = (line, field) => {
  if (field === 'quantity') return !isPositiveWholeNumber(line.quantity);
  if (field === 'unitCost') return !isCentAmount(line.unitCost);
  if (field === 'description') return false;
  return isBlank(line[field]);
};

export const lineFieldHelperText = (line, field) => {
  if (field === 'quantity') {
    if (isBlank(line.quantity)) return 'Required';
    if (!isWholeNumber(line.quantity)) return 'Must be a whole number';
    if (!isPositiveWholeNumber(line.quantity)) return 'Must be at least 1';
    return '';
  }
  if (field === 'unitCost') {
    if (isBlank(line.unitCost)) return 'Required';
    if (!isDecimalNumber(line.unitCost)) return 'Must be a valid number';
    if (!isCentAmount(line.unitCost)) return 'At most 2 decimal places';
    return '';
  }
  if (field === 'description') return '';
  return isBlank(line[field]) ? 'Required' : '';
};

const LINE_FIELDS = ['drugName', 'ndc', 'description', 'quantity', 'unitCost'];

export const isLineValid = (line) => LINE_FIELDS.every((field) => !lineFieldError(line, field));

// Word-boundary so this matches "paid"/"void" (any casing) without also matching "unpaid".
const LOCKED_STATUS_PATTERN = /\b(paid|void)\b/i;

export const isInvoiceLocked = (invoiceStatus) => LOCKED_STATUS_PATTERN.test(invoiceStatus ?? '');
